<script setup lang="ts">
import { useId } from "vue";

/** A page section: title and one-line note in the left margin, content beside it. */
defineProps<{ title: string; note?: string }>();
const headingId = useId();
</script>

<template>
  <section class="block" :aria-labelledby="headingId">
    <header class="block__rail">
      <h2 :id="headingId">{{ title }}</h2>
      <p v-if="note" class="block__note">{{ note }}</p>
      <slot name="rail" />
    </header>
    <div class="block__body"><slot /></div>
  </section>
</template>

<style scoped>
.block {
  display: grid;
  grid-template-columns: 10.5rem minmax(0, 1fr);
  column-gap: clamp(1.5rem, 4vw, 3.5rem);
  padding-block: 2.75rem;
  border-top: 1px solid var(--rule);
}
.block__rail { position: sticky; top: 1.5rem; align-self: start; min-width: 0; }
.block__rail h2 { font-size: 1.0625rem; line-height: 1.4; letter-spacing: 0.01em; }
.block__note { margin-top: 0.3rem; color: var(--ink-3); font-size: 0.8125rem; line-height: 1.5; }
.block__body { min-width: 0; }

/* Sections settle in one after another when the first observation arrives. */
@media (prefers-reduced-motion: no-preference) {
  .block { animation: settle 520ms cubic-bezier(0.2, 0.7, 0.2, 1) both; }
  .block:nth-of-type(2) { animation-delay: 70ms; }
  .block:nth-of-type(3) { animation-delay: 140ms; }
}
@keyframes settle { from { opacity: 0; transform: translateY(0.6rem); } }

@media (max-width: 860px) {
  .block { grid-template-columns: minmax(0, 1fr); row-gap: 1.25rem; padding-block: 2.25rem; }
  .block__rail {
    position: static;
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 1rem;
    align-items: baseline;
    justify-content: space-between;
  }
  .block__note { margin-top: 0; }
}
</style>
